import { create } from "zustand";

import type { FunnelStep } from "~/shared/utils/types";

const STORAGE_KEY = "funnel_session";
const SESSION_EXPIRY_HOURS = 24;

interface FunnelStoreState {
  session_id: string | null;
  healthie_id: string | null;
  current_step: FunnelStep | null;
  step_started_at: string | null;
  step_position: number | null;
  started_at: string | null;

  // Actions
  getOrCreateSession: () => string;
  startStep: (step: FunnelStep) => void;
  setStepPosition: (position: number) => void;
  setHealthieId: (id: string) => void;
  resetSession: () => void;
  isSessionExpired: () => boolean;
}

const generateSessionId = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const isSessionExpired = (startedAt: string): boolean => {
  const started = new Date(startedAt).getTime();
  const now = Date.now();
  const expiryMs = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
  return now - started > expiryMs;
};

const loadFromStorage = (): Partial<FunnelStoreState> | null => {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);

      // Check expiry
      if (parsed.started_at && isSessionExpired(parsed.started_at)) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return parsed;
    }
  } catch (e) {
    console.error("[FunnelStore] Error loading from storage:", e);
  }
  return null;
};

const saveToStorage = (state: Partial<FunnelStoreState>): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        session_id: state.session_id,
        healthie_id: state.healthie_id,
        current_step: state.current_step,
        step_started_at: state.step_started_at,
        step_position: state.step_position,
        started_at: state.started_at,
      }),
    );
  } catch (e) {
    console.error("[FunnelStore] Error saving to storage:", e);
  }
};

// Load initial state from localStorage
const storedState = loadFromStorage();

export const useFunnelStore = create<FunnelStoreState>((set, get) => ({
  session_id: storedState?.session_id ?? null,
  healthie_id: storedState?.healthie_id ?? null,
  current_step: storedState?.current_step ?? null,
  step_started_at: storedState?.step_started_at ?? null,
  step_position: storedState?.step_position ?? null,
  started_at: storedState?.started_at ?? null,

  getOrCreateSession: () => {
    const state = get();

    // Return existing valid session
    if (
      state.session_id &&
      state.started_at &&
      !isSessionExpired(state.started_at)
    ) {
      return state.session_id;
    }

    // Create new session
    const newSessionId = generateSessionId();
    const now = new Date().toISOString();

    set({
      session_id: newSessionId,
      started_at: now,
      healthie_id: null,
      current_step: null,
      step_started_at: null,
      step_position: null,
    });

    saveToStorage({
      session_id: newSessionId,
      started_at: now,
      healthie_id: null,
      current_step: null,
      step_started_at: null,
      step_position: null,
    });

    return newSessionId;
  },

  startStep: (step: FunnelStep) => {
    const now = new Date().toISOString();
    const state = get();

    set({
      current_step: step,
      step_started_at: now,
    });

    saveToStorage({
      ...state,
      current_step: step,
      step_started_at: now,
    });
  },

  setStepPosition: (position: number) => {
    const state = get();
    if (state.step_position === position) return;

    set({ step_position: position });

    saveToStorage({
      ...state,
      step_position: position,
    });
  },

  setHealthieId: (id: string) => {
    const state = get();

    set({ healthie_id: id });

    saveToStorage({
      ...state,
      healthie_id: id,
    });
  },

  isSessionExpired: () => {
    const state = get();
    if (!state.session_id || !state.started_at) return true;
    return isSessionExpired(state.started_at);
  },

  resetSession: () => {
    const newSessionId = generateSessionId();
    const now = new Date().toISOString();

    const newState = {
      session_id: newSessionId,
      started_at: now,
      healthie_id: null,
      current_step: null,
      step_started_at: null,
      step_position: null,
    };

    set(newState);
    saveToStorage(newState);
  },
}));
